import React, { useEffect, useState } from 'react'
import HeaderBanner from '../ui/HeaderBanner'
import client from '../../client/Client'
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Typography,
  Timeline,
  TimelineItem,
  TimelineConnector,
  TimelineHeader,
  TimelineIcon,
  TimelineBody,
} from "@material-tailwind/react";

// === INTERFACES (include the new Paragraph types) ===

interface SpecialSession {
  _type: string;
  Title: string;
  OrganizedBY: Organizer[];
  YearVersion: string;
  CFPFSS: CFPFSSBlock[];
  PotentialContributingAuthors: Author[];
  PotentialReviewers: Reviewer[];
  TopicCovered: TopicCoveredBlock[];
  SSObjectives: SSObjectivesBlock[];
  SpecialNote: SpecialNoteBlock[];
  _id: string;
  _upUniversitydAt: string;
  _createdAt: string;
  _rev: string;
  CallForPapersSS?: CallForPapersFile;
  SubmissionSSLink?: string;
  Deadlines?: DeadlineSS[];
  SubmissionNotes?: SubmissionNoteBlock[];

  // NEW:
  Paragraph?: ParagraphBlock[];
}

interface Organizer {
  children: OrganizerName[];
  _type: string;
}

interface OrganizerName {
  _type: string;
  marks: string[];
  text: string;
}

interface Author {
  _type: string;
  style: string;
  _key: string;
  markDefs: string[];
  children: AuthorInfo[];
  level?: number;
  listItem?: string;
}

interface AuthorInfo {
  _type: string;
  marks: string[];
  text: string;
}

interface TopicCoveredBlock {
  _type: string;
  style: string;
  _key: string;
  markDefs: string[];
  children: TopicCoveredContent[];
}

interface TopicCoveredContent {
  _type: string;
  marks: string[];
  text: string;
}

interface SSObjectivesBlock {
  style: string;
  _key: string;
  markDefs: string[];
  children: SSObjectivesContent[];
  _type: string;
}

interface SSObjectivesContent {
  _type: string;
  marks: string[];
  text: string;
}

interface SpecialNoteBlock {
  children: SpecialNoteContent[];
  _type: string;
  style: string;
  _key: string;
  markDefs: string[];
}

interface SpecialNoteContent {
  _type: string;
  marks: string[];
  text: string;
}

interface Reviewer {
  _type: string;
  _key: string;
  Name: string;
  Email: string;
  University: string;
}

interface CFPFSSBlock {
  _type: string;
  style: string;
  _key: string;
  markDefs: string[];
  children: CFPFSSContent[];
  listItem?: string;
  level?: number;
}

interface CFPFSSContent {
  _type: string;
  marks: string[];
  text: string;
}

interface SanityFileAssetRef {
  _type: string;           // "reference"
  _ref: string;            // e.g. "file-xxxx-yyy"
}

interface CallForPapersFile {
  _type: string;           // "file"
  asset: SanityFileAssetRef;
}

interface DeadlineSS {
  _type: string;           // "DeadlinesSS"
  _key: string;
  DeadlineSS: string;      // e.g. "Deadline Paper Submission"
  DateSS: string;          // e.g. "December 15, 2025"
}

interface SubmissionNoteBlock {
  _type: string;
  style: string;
  _key: string;
  markDefs: any[];
  children: {
    _type: string;
    marks: string[];
    text: string;
    _key: string;
  }[];
}

// NEW: Paragraph types
interface ParagraphSpan {
  _type: string; // "span"
  _key: string;
  marks: string[];
  text: string;
}

interface ParagraphContentBlock {
  _type: string;       // "block"
  style: string;
  _key: string;
  markDefs: any[];
  children: ParagraphSpan[];
}

interface ParagraphBlock {
  _type: string;       // "Paragraphs"
  _key: string;
  ParagraphTitle: string;
  Content: ParagraphContentBlock[];
}

// === HELPERS ===

const getFileUrlFromSanityRef = (fileField?: CallForPapersFile): string | undefined => {
  if (!fileField?.asset?._ref) return undefined;

  // file-<id>-<extension>
  const [, id, extension] = fileField.asset._ref.split("-");
  if (!id || !extension) return undefined;

  return `https://cdn.sanity.io/files/79vgbaej/production/${id}.${extension}`;
};

const renderMarkedChildren = (block: { children: any[]; markDefs?: any[] }) => {
  return block.children.map((child: any, childIdx: number) => {
    const linkData =
      child.marks && child.marks.length > 0
        ? block.markDefs?.find((def: any) => def._key === child.marks[0])
        : null;

    const hasStrong = child.marks?.includes('strong');
    const hasUnderline = child.marks?.includes('underline');

    let textClasses = '';
    if (hasStrong && hasUnderline) {
      textClasses = 'font-semibold text-red-600';
    } else if (hasStrong) {
      textClasses = 'font-semibold';
    } else if (hasUnderline) {
      textClasses = 'underline';
    }

    const content = (
      <span key={childIdx} className={textClasses}>
        {child.text}
      </span>
    );

    if (linkData) {
      return (
        <a
          key={childIdx}
          href={linkData.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-901 hover:text-blue-800 font-medium underline transition-colors duration-200"
        >
          {content}
        </a>
      );
    }

    return content;
  });
};

// === COMPONENT ===

const SpecialSessionsForReal = () => {
  const [data, setData] = useState<SpecialSession[]>()

  useEffect(() => {
    client.fetch('*[_type == "SSessions"]')
      .then((result) => {
        setData(result);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }, [])

  const [open, setOpen] = React.useState(0);
  const handleOpen = (value: any) => setOpen(open === value ? 0 : value);

  const TABLE_HEAD = ["Name", "Email", "University"];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 ">
      <div className="fixed left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none -z-1" aria-hidden="true">
        <svg width="1360" height="578" viewBox="0 0 1360 578" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="illustration-01">
              <stop stopColor="#FFF" offset="0%" />
              <stop stopColor="#EAEAEA" offset="77.402%" />
              <stop stopColor="#DFDFDF" offset="100%" />
            </linearGradient>
          </defs>
          <g fill="url(#illustration-01)" fillRule="evenodd">
            <circle cx="1232" cy="128" r="128" />
            <circle cx="155" cy="443" r="64" />
          </g>
        </svg>
      </div>

      <HeaderBanner title='Special Sessions' />

      <div className='mb-12 '>
        {data && data.map((session, index) => {
          const callForPapersUrl = getFileUrlFromSanityRef(session.CallForPapersSS);

          return (
            <Accordion key={session._id} open={open === index + 1}>
              <AccordionHeader
                onClick={() => handleOpen(index + 1)}
                className="
                  px-6 py-4
                  bg-white
                  rounded-lg
                  shadow-md
                  border border-gray-200
                  text-gray-800
                  font-semibold
                  text-lg
                  transition-all duration-300
                  hover:shadow-lg
                  hover:bg-red-50
                  hover:border-red-500
                  flex justify-between items-center relative
                "
              >
                <span className="pr-8">
                  Session {index + 1} : {session.Title}
                </span>

                <svg
                  className={`w-5 h-5 absolute right-4 top-1/2 transform -translate-y-1/2 transition-transform duration-300 ${
                    open === index + 1 ? "rotate-180 text-red-600" : "text-gray-600"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </AccordionHeader>

              <AccordionBody>
                <div className='space-y-8'>

                  {/* === BUTTONS SECTION (Call for Papers + Submission Link) === */}
                  {(callForPapersUrl || session.SubmissionSSLink) && (
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto'>
                      {callForPapersUrl && (
                        <a
                          href={callForPapersUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className='bg-white border-2 border-blue-901 rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:bg-blue-901 group text-center block'
                        >
                          <span className='text-blue-901 group-hover:text-white font-semibold text-lg'>
                            Call for Papers (PDF)
                          </span>
                        </a>
                      )}

                      {session.SubmissionSSLink && (
                        <a
                          href={session.SubmissionSSLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className='bg-white border-2 border-blue-901 rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:bg-blue-901 group text-center block'
                        >
                          <span className='text-blue-901 group-hover:text-white font-semibold text-lg'>
                            Submission Link
                          </span>
                        </a>
                      )}
                    </div>
                  )}

                  {/* === Organized By === */}
                  {session.OrganizedBY && session.OrganizedBY.length > 0 && (
                    <div className="bg-gray-50 border-l-4 border-blue-901 p-6 rounded-lg shadow-sm">
                      <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-4">
                        Organized By
                      </h1>

                      <div className="space-y-3">
                        {session.OrganizedBY.map((items, idx) => (
                          <div
                            key={idx}
                            className="text-gray-800 hover:text-white bg-white border border-gray-200 p-4 rounded-md shadow-sm hover:shadow-md hover:bg-red-500 transition-all duration-300"
                          >
                            <p className="leading-relaxed">
                              {items.children?.map((child: any) => child.text).join(' ')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* === GENERIC PARAGRAPHS (replace CFPFSS / Topics / Notes / Objectives / etc.) === */}
                  {session.Paragraph && session.Paragraph.length > 0 && (
                    <div className="space-y-6">
                      {session.Paragraph.map((para) => (
                        <section
                          key={para._key}
                          className="bg-gray-50 border-l-4 border-blue-901 p-6 rounded-lg shadow-sm"
                        >
                          <h2 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-4">
                            {para.ParagraphTitle}
                          </h2>
                          <div className="space-y-3">
                            {para.Content?.map((block) => (
                              <p
                                key={block._key}
                                className="text-gray-700 leading-relaxed"
                              >
                                {renderMarkedChildren(block)}
                              </p>
                            ))}
                          </div>
                        </section>
                      ))}
                    </div>
                  )}

                  {/* === Potential Reviewers (Table + Mobile Cards) === */}
                  {session.PotentialReviewers && session.PotentialReviewers.length > 0 && (
                    <div className="bg-gray-50 border-l-4 border-blue-901 p-6 rounded-lg shadow-sm">
                      <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-4">
                        Potential Reviewers
                      </h1>

                      {/* DESKTOP TABLE */}
                      <div className="hidden md:block bg-white border border-gray-200 rounded-lg shadow-sm">
                        <table className="w-full min-w-max table-auto text-left">
                          <thead>
                            <tr>
                              {TABLE_HEAD.map((head) => (
                                <th
                                  key={head}
                                  className="border-b border-blue-gray-100 bg-blue-gray-50 p-4"
                                >
                                  <Typography
                                    variant="small"
                                    color="blue-gray"
                                    className="font-normal leading-none opacity-70"
                                  >
                                    {head}
                                  </Typography>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {session.PotentialReviewers.map(
                              ({ Name, Email, University }, idx) => {
                                const isLast =
                                  idx === session.PotentialReviewers.length - 1;
                                const classes = isLast
                                  ? "p-4"
                                  : "p-4 border-b border-blue-gray-50";

                                return (
                                  <tr
                                    key={Name + idx}
                                    className="hover:bg-red-50 transition-colors duration-200"
                                  >
                                    <td className={classes}>
                                      <Typography
                                        variant="small"
                                        color="blue-gray"
                                        className="font-normal"
                                      >
                                        {Name}
                                      </Typography>
                                    </td>
                                    <td className={`${classes} bg-blue-gray-50/50`}>
                                      <Typography
                                        variant="small"
                                        color="blue-gray"
                                        className="font-normal break-all"
                                      >
                                        {Email.trim()}
                                      </Typography>
                                    </td>
                                    <td className={classes}>
                                      <Typography
                                        variant="small"
                                        color="blue-gray"
                                        className="font-normal"
                                      >
                                        {University}
                                      </Typography>
                                    </td>
                                  </tr>
                                );
                              }
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* MOBILE CARDS */}
                      <div className="md:hidden space-y-3 mt-4">
                        {session.PotentialReviewers.map(
                          ({ Name, Email, University, _key }, idx) => (
                            <div
                              key={_key ?? `${Name}-${idx}`}
                              className="
                                bg-white border border-gray-200 rounded-md shadow-sm
                                p-4
                                hover:bg-red-50 hover:shadow-md
                                transition-all duration-300
                              "
                            >
                              <p className="text-xs uppercase font-semibold text-gray-500">
                                Name
                              </p>
                              <p className="text-gray-800 font-medium mb-3">
                                {Name}
                              </p>

                              <p className="text-xs uppercase font-semibold text-gray-500">
                                Email
                              </p>
                              <p className="text-red-600 underline break-all mb-3">
                                <a href={`mailto:${Email.trim()}`}>
                                  {Email.trim()}
                                </a>
                              </p>

                              <p className="text-xs uppercase font-semibold text-gray-500">
                                University
                              </p>
                              <p className="text-gray-800">
                                {University}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* === DEADLINES (Timeline) === */}
                  {session.Deadlines && session.Deadlines.length > 0 && (
                    <div className="bg-gray-50 border-l-4 border-blue-901 p-6 rounded-lg shadow-sm">
                      <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-800 mb-4">
                        Deadlines
                      </h1>

                      <Timeline>
                        {session.Deadlines.map((deadline) => (
                          <TimelineItem key={deadline._key}>
                            <TimelineConnector />
                            <TimelineHeader className="h-3">
                              <TimelineIcon className="bg-red-600" />
                              <Typography
                                variant="h6"
                                color="blue-gray"
                                className="leading-none"
                              >
                                {deadline.DateSS}
                              </Typography>
                            </TimelineHeader>
                            <TimelineBody className="pb-8">
                              <Typography
                                variant="small"
                                color="gray"
                                className="font-normal text-gray-600"
                              >
                                {deadline.DeadlineSS}
                              </Typography>
                            </TimelineBody>
                          </TimelineItem>
                        ))}
                      </Timeline>
                    </div>
                  )}
                </div>
              </AccordionBody>
            </Accordion>
          )
        })}
      </div>
    </div>
  )
}

export default SpecialSessionsForReal
